// @flow
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography} from '@material-ui/core';
import {commaFormat} from '../../../../utils/number';
import {getImageUrl} from '../../../../utils/image_provider';
import {classNames} from '../../../../utils/react';
import {colors} from '../../../../assets/styles/colors';
import type {
  ManualOrderProductOptionGroup,
  SelectedProductOptions,
  ValidationError,
} from '../../types/productOptions';

const useStyles = makeStyles(theme => ({
  optionSwatchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
    gap: '8px',
  },
  optionSwatch: {
    width: '60px',
    height: '60px',
    border: `2px solid ${colors.grey300}`,
    borderRadius: '.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      borderColor: colors.purple3,
      transform: 'scale(1.05)',
    },
  },
  optionSwatchSelected: {
    borderColor: colors.purple3,
    borderWidth: '2px',
    padding: '.125rem',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '6px',
      right: '6px',
      width: '12px',
      height: '12px',
      backgroundColor: colors.purple3,
      borderRadius: '50%',
    },
  },
  optionSwatchImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '.75rem',
  },
  optionSwatchColor: {
    width: '100%',
    height: '100%',
    borderRadius: '.75rem',
    overflow: 'hidden',
  },
  optionSwatchText: {
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'center',
    padding: '4px',
    lineHeight: 1.2,
  },
}));

export type BaseOptionRendererProps = {
  group: ManualOrderProductOptionGroup,
  selectedProductOptions: SelectedProductOptions,
  currencySymbol: string,
  validationErrors?: ValidationError[],
  onOptionToggle: (groupId: string, optionId: string) => void,
  onTextChange: (groupId: string, optionId: string, value: string) => void,
  onQuantityChange: (
    groupId: string,
    optionId: string,
    quantity: number
  ) => void,
};

type Props = BaseOptionRendererProps;

function GridOptionRenderer({
  group,
  selectedProductOptions,
  currencySymbol,
  onOptionToggle,
}: Props) {
  const classes = useStyles();
  const groupId = group.id;

  return (
    <div className={classes.optionSwatchGrid}>
      {group.options.map(option => {
        if (!option.enabled) return null;
        const optionId = option.id;
        const selectedOption = selectedProductOptions[groupId]?.[optionId];
        const isSelected = selectedOption?.selected || false;

        const imageId = option.storeProductOption.imageId;
        const color = option.storeProductOption.color;
        const hasImage = !!imageId;
        const hasColor = !!color;
        const imageSrc = getImageUrl(imageId);

        const showThumb = hasImage || hasColor;

        return (
          <div
            key={optionId}
            className={classNames(classes.optionSwatch, {
              [classes.optionSwatchSelected]: isSelected,
            })}
            onClick={() => onOptionToggle(groupId, optionId)}
            title={`${option.storeProductOption.name}: +${commaFormat(
              parseFloat(option.price || 0),
              currencySymbol
            )}`}
          >
            {showThumb ? (
              <div
                className={classes.optionSwatchColor}
                style={{
                  backgroundColor: color,
                }}
              >
                <img
                  src={imageSrc}
                  alt=""
                  className={classes.optionSwatchImage}
                />
              </div>
            ) : (
              <Typography className={classes.optionSwatchText}>
                {option.storeProductOption.name}
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default GridOptionRenderer;
